import { sectionRenderer } from '@theme/section-renderer';
import {
  compareReviewDatesNewestFirst,
  getReviewPageBounds,
  getReviewPageCount,
  getVisibleReviewPages,
} from '@theme/product-reviews-pagination';

const REVIEW_ITEM_SELECTOR = '[data-review-item]';

class ProductReviewsNative extends HTMLElement {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #pagination = null;

  /** @type {HTMLElement | null} */
  #pages = null;

  /** @type {HTMLButtonElement | null} */
  #previous = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {Set<string>} */
  #reviewKeys = new Set();

  /** @type {number} */
  #currentPage = 1;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.#grid = this.querySelector('[data-review-grid]');
    this.#viewport = this.querySelector('[data-review-viewport]');
    this.#pagination = this.querySelector('[data-review-pagination]');
    this.#pages = this.querySelector('[data-review-pages]');
    this.#previous = this.querySelector('[data-review-previous]');
    this.#next = this.querySelector('[data-review-next]');

    if (!this.#grid || !this.#viewport || !this.#pagination || !this.#pages || !this.#previous || !this.#next) return;

    this.#collectItems(this);
    this.#previous.setAttribute('aria-label', this.#pagination.dataset.previousLabel || 'Previous');
    this.#next.setAttribute('aria-label', this.#pagination.dataset.nextLabel || 'Next');
    this.#previous.addEventListener('click', () => this.#goToPage(this.#currentPage - 1));
    this.#next.addEventListener('click', () => this.#goToPage(this.#currentPage + 1));

    if (this.#sortNewestFirst) {
      for (const item of this.#items) item.hidden = true;
    } else {
      this.#render();
    }
    this.#loadAllReviews();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #reviewsPerPage() {
    const configured = Number.parseInt(this.dataset.reviewsPerPage || '8', 10);
    return Number.isFinite(configured) ? Math.max(1, configured) : 8;
  }

  get #paginationEnabled() {
    return this.dataset.paginationEnabled === 'true';
  }

  get #sortNewestFirst() {
    return this.dataset.sortNewestFirst === 'true';
  }

  get #desktopColumns() {
    const configured = Number.parseInt(this.dataset.reviewColumns || '1', 10);
    return Number.isFinite(configured) ? Math.max(1, configured) : 1;
  }

  get #mobileColumns() {
    const configured = Number.parseInt(this.dataset.reviewColumnsMobile || '1', 10);
    return Number.isFinite(configured) ? Math.max(1, configured) : 1;
  }

  get #pageCount() {
    return getReviewPageCount(this.#items.length, this.#reviewsPerPage, this.#paginationEnabled);
  }

  async #loadAllReviews() {
    const source = this.querySelector('[data-review-source]');
    const section = this.closest('.shopify-section');
    if (!(source instanceof HTMLElement) || !(section instanceof HTMLElement)) {
      this.#finishLoading();
      return;
    }

    let nextUrl = source.dataset.nextUrl || '';
    const visited = new Set();
    this.setAttribute('aria-busy', 'true');

    try {
      while (nextUrl && !visited.has(nextUrl)) {
        if (this.#abortController.signal.aborted) return;

        visited.add(nextUrl);
        const url = new URL(nextUrl, window.location.origin);
        const html = await sectionRenderer.getSectionHTML(section.id, true, url);
        if (this.#abortController.signal.aborted) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const matchingComponent = Array.from(documentFragment.querySelectorAll('product-reviews-native')).find(
          (component) => component.dataset.blockId === this.dataset.blockId
        );
        if (!(matchingComponent instanceof HTMLElement)) throw new Error('Product reviews block missing from response');

        this.#collectItems(matchingComponent);
        const nextSource = matchingComponent.querySelector('[data-review-source]');
        nextUrl = nextSource instanceof HTMLElement ? nextSource.dataset.nextUrl || '' : '';
      }

      this.#finishLoading();
    } catch (_error) {
      this.querySelector('[data-review-load-error]')?.removeAttribute('hidden');
      this.#finishLoading();
    }
  }

  /**
   * @param {ParentNode} source
   */
  #collectItems(source) {
    if (!this.#grid) return;

    for (const item of source.querySelectorAll(REVIEW_ITEM_SELECTOR)) {
      if (!(item instanceof HTMLElement)) continue;
      const key = item.dataset.reviewKey || '';
      if (key && this.#reviewKeys.has(key)) continue;
      if (key) this.#reviewKeys.add(key);

      const reviewItem = source === this ? item : /** @type {HTMLElement} */ (item.cloneNode(true));
      if (source !== this) {
        if (this.#paginationEnabled) reviewItem.hidden = true;
        this.#grid.append(reviewItem);
      }
      this.#items.push(reviewItem);
    }
  }

  #finishLoading() {
    if (this.#sortNewestFirst) this.#sortItemsNewestFirst();
    this.removeAttribute('aria-busy');
    this.querySelector('[data-review-empty]')?.toggleAttribute('hidden', this.#items.length > 0);
    this.#currentPage = Math.min(this.#currentPage, this.#pageCount);
    this.#render();
  }

  #sortItemsNewestFirst() {
    if (!this.#grid) return;

    this.#items.sort((first, second) =>
      compareReviewDatesNewestFirst(first.dataset.reviewSubmittedAt || '', second.dataset.reviewSubmittedAt || '')
    );
    this.#grid.append(...this.#items);
  }

  /**
   * @param {number} page
   */
  #goToPage(page) {
    const targetPage = Math.min(Math.max(page, 1), this.#pageCount);
    if (targetPage === this.#currentPage) return;

    this.#currentPage = targetPage;
    this.#render();
    this.#viewport?.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    requestAnimationFrame(() => {
      this.#pages?.querySelector(`[data-review-page="${targetPage}"]`)?.focus({ preventScroll: true });
    });
  }

  #render() {
    if (!this.#pagination || !this.#pages || !this.#previous || !this.#next) return;

    const { start, end } = getReviewPageBounds(
      this.#items.length,
      this.#reviewsPerPage,
      this.#currentPage,
      this.#paginationEnabled
    );
    for (const [index, item] of this.#items.entries()) {
      const hidden = index < start || index >= end;
      item.toggleAttribute('hidden', hidden);
      if (hidden) {
        delete item.dataset.reviewDesktopRow;
        delete item.dataset.reviewDesktopColumn;
        delete item.dataset.reviewMobileRow;
        delete item.dataset.reviewMobileColumn;
        continue;
      }

      const position = index - start;
      item.dataset.reviewDesktopRow = Math.floor(position / this.#desktopColumns).toString();
      item.dataset.reviewDesktopColumn = (position % this.#desktopColumns).toString();
      item.dataset.reviewMobileRow = Math.floor(position / this.#mobileColumns).toString();
      item.dataset.reviewMobileColumn = (position % this.#mobileColumns).toString();
    }

    this.#pagination.toggleAttribute('hidden', !this.#paginationEnabled || this.#pageCount <= 1);
    this.#previous.disabled = this.#currentPage <= 1;
    this.#next.disabled = this.#currentPage >= this.#pageCount;
    this.#renderPageButtons();
    const pageLabel = (this.#pagination.dataset.pageLabel || 'Page __PAGE__').replace(
      '__PAGE__',
      this.#currentPage.toString()
    );
    const status = this.querySelector('[data-review-status]');
    if (status) status.textContent = pageLabel;
  }

  #renderPageButtons() {
    if (!this.#pages || !this.#pagination) return;
    this.#pages.replaceChildren();

    for (const page of this.#visiblePages()) {
      if (page === null) {
        const gap = document.createElement('span');
        gap.className = 'product-reviews-native__pagination-gap';
        gap.textContent = '…';
        gap.setAttribute('aria-hidden', 'true');
        this.#pages.append(gap);
        continue;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-reviews-native__pagination-button';
      button.dataset.reviewPage = page.toString();
      button.textContent = page.toString();
      button.setAttribute('aria-label', (this.#pagination.dataset.pageLabel || 'Page __PAGE__').replace('__PAGE__', page.toString()));
      if (page === this.#currentPage) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', () => this.#goToPage(page));
      this.#pages.append(button);
    }
  }

  /**
   * @returns {(number | null)[]}
   */
  #visiblePages() {
    return getVisibleReviewPages(this.#pageCount, this.#currentPage);
  }
}

if (!customElements.get('product-reviews-native')) {
  customElements.define('product-reviews-native', ProductReviewsNative);
}
